import {
  ESdChangeReason,
  type IComponent,
  type IDisplayItem,
  type IFilterItem,
  type IProject,
  type ITransformItem,
  type ITransitionItem,
  isGradient,
  StyleHelper
} from "@kuika/kuika-cl-model";
import { v4 as uuidv4 } from "uuid";
import { getValues, type ScreenDesignInstance } from "../../designer-context";
import { applyDesignerPropertyChange, applyPropertyGridChangeWithoutCommit } from "../../designer-property-actions";

type CategoryGroup = "button" | "input" | "container" | "display" | "data" | "chart" | "interactive" | "generic";
type StyleSection = "position" | "text" | "fill" | "border" | "shadow" | "effects";

const SECTION_INVISIBLE_MAP: Record<StyleSection, string> = {
  position: "layout",
  text: "text",
  fill: "fill",
  border: "border",
  shadow: "shadow",
  effects: "effects"
};

const POSITION_PROPERTY_MAP: Record<string, string> = {
  paddingTop: "layout_padding",
  paddingRight: "layout_padding",
  paddingBottom: "layout_padding",
  paddingLeft: "layout_padding",
  width: "layout_width",
  height: "layout_height",
  minWidth: "layout_minWidth",
  minHeight: "layout_minHeight",
  maxWidth: "layout_maxWidth",
  maxHeight: "layout_maxHeight",
  alignItems: "layout_alignItems",
  textAlign: "layout_textAlign",
  display: "layout_display",
  xAxis: "layout_xAxis",
  yAxis: "layout_yAxis"
};

const TEXT_PROPERTY_MAP: Record<string, string> = {
  fontFamily: "text_fontFamily",
  fontWeight: "text_fontWeight",
  fontSize: "text_fontSize",
  color: "text_color",
  lineHeight: "text_lineHeight",
  letterSpacing: "text_letterSpacing",
  textDecoration: "text_textDecoration",
  textTransform: "text_textTransform",
  dontAffectedMobileSystem: "text_dontAffectedMobileSystem"
};

const FILL_PROPERTY_MAP: Record<string, string> = {
  color: "fill_color",
  backgroundImage: "fill_backgroundImage",
  repeatSwitch: "fill_repeatSwitch",
  bgImageSize: "fill_bgImageSize",
  stretch: "fill_stretch",
  bgImagePosition: "fill_bgImagePosition"
};

const BORDER_PROPERTY_MAP: Record<string, string> = {
  borderColor: "border_borderColor",
  borderTopWidth: "border_borderWidth",
  borderRightWidth: "border_borderWidth",
  borderBottomWidth: "border_borderWidth",
  borderLeftWidth: "border_borderWidth",
  borderTopLeftRadius: "border_borderRadius",
  borderTopRightRadius: "border_borderRadius",
  borderBottomRightRadius: "border_borderRadius",
  borderBottomLeftRadius: "border_borderRadius",
  borderStyle: "border_borderStyle"
};

const SHADOW_PROPERTY_MAP: Record<string, string> = {
  color: "shadow_color",
  x: "shadow_structure",
  y: "shadow_structure",
  b: "shadow_structure",
  s: "shadow_structure"
};

// Effects field → inVisibleStyles key reference (for grep-friendly
// documentation; not consumed at runtime because each canApply() call
// site passes the literal visibility key directly, mirroring how
// other style sections handle their own gates):
//
//   opacity / cursor / pointerEvents / mixBlendMode → effects_opacity /
//     effects_cursor / effects_pointerEvents / effects_blending
//   outlineStyle / outlineWidth / outlineColor / outlineOffset →
//     effects_outline (one shared visibility key, mirrors
//     shadow_structure's grouping pattern)
//   transforms / transitions / transformOriginX/Y / perspective /
//     childrenPerspective{,OriginX,OriginY} / backfaceVisibility /
//     transformStyle → effects_transforms (Faz 3)
//   transitions → effects_transitions (Faz 3, separate key from
//     transforms despite the shared multi-item shape)
//   filters → effects_filters, backdropFilters → effects_backdrop_filters
//     (Faz 4)
//
// If a future inVisibleStyles flavour wants per-sub-field granularity
// (e.g. hide just outlineColor), split the outline key into 4 keys at
// every canApply("effects", "effects_outline") call site in
// applyCssStylesToComponent below.

/**
 * Determines if a hex/rgba color is "dark" (relative luminance < 0.4).
 * Used to auto-select dark preset for children of dark containers.
 */
export function isDarkColor(color: string): boolean {
  if (!color || color === "transparent") return false;

  let r = 0,
    g = 0,
    b = 0;

  // Parse hex
  if (color.startsWith("#")) {
    const hex = color.replace("#", "");
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length >= 6) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    } else {
      return false;
    }
  }
  // Parse rgba/rgb
  else if (color.startsWith("rgb")) {
    const match = color.match(/(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    if (match) {
      r = parseInt(match[1], 10);
      g = parseInt(match[2], 10);
      b = parseInt(match[3], 10);
    } else {
      return false;
    }
  } else {
    return false;
  }

  // Relative luminance (simplified sRGB)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.4;
}

/**
 * Parses a CSS color string into [r, g, b, a].
 * Supports #RGB, #RRGGBB, #RRGGBBAA, rgb(), rgba(), and "transparent".
 * Alpha defaults to 1 if not present.
 */
export function parseColor(color: string): [number, number, number, number] | null {
  if (!color || color === "transparent") return [0, 0, 0, 0];
  let r = 0,
    g = 0,
    b = 0,
    a = 1;
  if (color.startsWith("#")) {
    const hex = color.replace("#", "");
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length === 6) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    } else if (hex.length === 8) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
      a = parseInt(hex.substring(6, 8), 16) / 255;
    } else {
      return null;
    }
  } else if (color.startsWith("rgba")) {
    const match = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*([\d.]+)\s*\)/);
    if (match) {
      r = parseInt(match[1], 10);
      g = parseInt(match[2], 10);
      b = parseInt(match[3], 10);
      a = parseFloat(match[4]);
    } else {
      return null;
    }
  } else if (color.startsWith("rgb")) {
    const match = color.match(/(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    if (match) {
      r = parseInt(match[1], 10);
      g = parseInt(match[2], 10);
      b = parseInt(match[3], 10);
    } else {
      return null;
    }
  } else {
    return null;
  }
  return [r, g, b, a];
}

/**
 * Alpha-composites a foreground RGBA color over an opaque background RGB color.
 * Returns [r, g, b] as integers.
 */
function alphaComposite(fg: [number, number, number, number], bg: [number, number, number]): [number, number, number] {
  const a = fg[3];
  return [
    Math.round(fg[0] * a + bg[0] * (1 - a)),
    Math.round(fg[1] * a + bg[1] * (1 - a)),
    Math.round(fg[2] * a + bg[2] * (1 - a))
  ];
}

/**
 * Walks up the parent chain from a component to resolve the effective
 * rendered background color, compositing translucent layers over ancestors.
 * Falls back to white (#FFFFFF) if no opaque ancestor is found.
 *
 * When `dictionary` is provided (the full screenDesignDictionary), intermediate
 * wrapper nodes that may not appear in the collected `components` array are
 * also available for parent lookups, preventing broken parent chains.
 */
export function resolveEffectiveBackground(
  kuikaId: string,
  components: Array<{ kuikaId: string; parentId: string | null; styleObj: any }>,
  ownBgColor?: string,
  dictionary?: Record<number, any>
): string {
  const compMap = new Map<string, { parentId: string | null; styleObj: any }>();
  for (const c of components) compMap.set(c.kuikaId, c);

  // Supplement with dictionary entries to cover intermediate wrapper nodes
  // that may not have been collected via the children-tree walk
  if (dictionary) {
    for (const key of Object.keys(dictionary)) {
      if (!compMap.has(key)) {
        const di = dictionary[Number(key)];
        if (di) {
          compMap.set(key, {
            parentId: di.parentId != null ? di.parentId.toString() : null,
            styleObj: di.styleObj || null
          });
        }
      }
    }
  }

  // Collect background colors from current component up to root
  const bgLayers: Array<[number, number, number, number]> = [];

  // Start with the component's own bg if provided
  if (ownBgColor) {
    const parsed = parseColor(ownBgColor);
    if (parsed) bgLayers.push(parsed);
  }

  // Walk parents
  let current = compMap.get(kuikaId);
  let parentId = current?.parentId;
  while (parentId) {
    const parent = compMap.get(parentId);
    if (!parent) break;
    const parentBg = parent.styleObj?.fill?.color;
    if (parentBg && parentBg !== "transparent") {
      const parsed = parseColor(parentBg);
      if (parsed) bgLayers.push(parsed);
    }
    parentId = parent.parentId;
  }

  // Composite from deepest ancestor (last in array) forward to the component (first)
  // Start with white as the base
  let base: [number, number, number] = [255, 255, 255];

  for (let i = bgLayers.length - 1; i >= 0; i--) {
    const layer = bgLayers[i];
    if (layer[3] >= 1) {
      // Fully opaque — replaces everything below
      base = [layer[0], layer[1], layer[2]];
    } else if (layer[3] > 0) {
      base = alphaComposite(layer, base);
    }
    // alpha === 0 → skip (fully transparent)
  }

  // Convert to hex
  const toHex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${toHex(base[0])}${toHex(base[1])}${toHex(base[2])}`;
}

function relativeLuminance(r: number, g: number, b: number): number {
  const [rs, gs, bs] = [r / 255, g / 255, b / 255].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Calculates WCAG contrast ratio between two colors.
 * Returns a ratio number (e.g. 4.5 for AA compliance).
 */
export function getContrastRatio(color1: string, color2: string): number {
  const c1 = parseColor(color1);
  const c2 = parseColor(color2);
  if (!c1 || !c2) return 21; // assume max contrast if can't parse

  const l1 = relativeLuminance(c1[0], c1[1], c1[2]);
  const l2 = relativeLuminance(c2[0], c2[1], c2[2]);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) {
    const v = Math.round(l * 255);
    return [v, v, v];
  }
  const hue2rgb = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    Math.round(hue2rgb(p, q, h + 1 / 3) * 255),
    Math.round(hue2rgb(p, q, h) * 255),
    Math.round(hue2rgb(p, q, h - 1 / 3) * 255)
  ];
}

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Finds an accessible foreground color by adjusting lightness while preserving hue and saturation.
 * Uses binary search from the original lightness toward the boundary for minimal color change.
 * Returns the adjusted hex color, or null if no solution found.
 */
export function findAccessibleForeground(fgHex: string, bgHex: string, targetRatio: number): string | null {
  const fg = parseColor(fgHex);
  const bg = parseColor(bgHex);
  if (!fg || !bg) return null;

  const [h, s, origL] = rgbToHsl(fg[0], fg[1], fg[2]);
  const bgLum = relativeLuminance(bg[0], bg[1], bg[2]);

  const contrastAt = (l: number): number => {
    const [r, g, b] = hslToRgb(h, s, l);
    const fgLum = relativeLuminance(r, g, b);
    const lighter = Math.max(fgLum, bgLum);
    const darker = Math.min(fgLum, bgLum);
    return (lighter + 0.05) / (darker + 0.05);
  };

  const EPSILON = 0.001;

  const tryDirection = (boundaryL: number): string | null => {
    // Check if the boundary extreme can meet the target at all
    if (contrastAt(boundaryL) < targetRatio) return null;
    // If original lightness already meets target, return original color
    if (contrastAt(origL) >= targetRatio) return rgbToHex(fg[0], fg[1], fg[2]);

    // Binary search between origL and boundaryL for minimal lightness change
    let lo = origL;
    let hi = boundaryL;
    while (Math.abs(hi - lo) > EPSILON) {
      const mid = (lo + hi) / 2;
      if (contrastAt(mid) >= targetRatio) {
        hi = mid; // mid works, try closer to original
      } else {
        lo = mid; // mid doesn't work, go further from original
      }
    }

    const [r, g, b] = hslToRgb(h, s, hi);
    return rgbToHex(r, g, b);
  };

  // Prefer darkening for light backgrounds, lightening for dark backgrounds
  const preferDarken = bgLum > 0.5;
  const result = tryDirection(preferDarken ? 0 : 1);
  if (result) return result;
  // Fallback to opposite direction
  return tryDirection(preferDarken ? 1 : 0);
}

export function getComponentDef(sd: ScreenDesignInstance, di: IDisplayItem): IComponent | null {
  const componentDefs = sd.projectProvider.state.project?.componentSetObj?.components;
  if (!componentDefs) return null;
  return componentDefs.find((c) => c.id === di.item.id) || null;
}

export function isStyleVisible(inVisibleStyles: string[] | undefined | null, part: string): boolean {
  if (!inVisibleStyles || inVisibleStyles.length === 0) return true;
  return !inVisibleStyles.includes(part);
}

export function getCategoryGroup(componentCategory: string | null | undefined): CategoryGroup {
  if (!componentCategory) return "generic";
  switch (componentCategory) {
    case "Navigation":
      return "button";
    case "Text Input":
    case "Numeric Input":
    case "Date and Time Input":
    case "Select Input":
    case "CalendarInput":
    case "Input":
    case "SpecialInput":
      return "input";
    case "Container":
      return "container";
    case "Display":
      return "display";
    case "Data":
      return "data";
    case "Chart":
      return "chart";
    case "Interactive":
      return "interactive";
    case "Special":
    case "Trigger":
    case "Presentation":
      return "generic";
    default:
      return "generic";
  }
}

function deepClone<T>(obj: T): T {
  if (obj === null || obj === undefined) return obj;
  return JSON.parse(JSON.stringify(obj));
}

export type StyleState = "hover" | "pressed" | "focused" | "disabled";

/**
 * Canonical iteration order for the 4 state layers. Exported so designer-side
 * callers (designer-style-actions, component-tools handlers) don't each
 * declare their own `["hover","pressed","focused","disabled"]` array — drift
 * here would silently change apply order across the codebase.
 */
export const STATE_KEYS: readonly StyleState[] = ["hover", "pressed", "focused", "disabled"];

const STATE_KEY_MAP: Record<StyleState, "hoverStyles" | "pressedStyles" | "focusedStyles" | "disabledStyles"> = {
  hover: "hoverStyles",
  pressed: "pressedStyles",
  focused: "focusedStyles",
  disabled: "disabledStyles"
};

// Mirrors STATE_ALLOWED_PROPS in styling-window.tsx — the same UI-side allowlist
// that decides which base properties can be persisted into a state sub-object.
// Includes the *Style siblings (widthStyle, fontSizeStyle, ...) because those
// always travel with their numeric counterparts when present.
//
// !!! DRIFT WARNING !!! This list is duplicated verbatim in two LLM-facing
// surfaces and one developer-facing skill doc — keep all four in sync when
// adding / removing properties:
//   - mcp-chat-server/src/tools/definitions/cat-style.ts  (description block,
//     shown to the LLM as part of the `ui_style.set` tool schema)
//   - mcp-chat-server/src/agents/designer/skills/dsl-grammar.md  (cross-ref)
//   - .claude/skills/kuika-mcp-orchestrator/SKILL.md  ("Styling" section table)
// The list cannot easily be auto-generated across the designer / mcp-chat-server
// package boundary without lifting it into kuika-cl-model, which would invert
// the dependency. Out-of-band sync is the current contract.
const STATE_ALLOWED_PROPS_BY_SECTION: Record<string, Set<string>> = {
  position: new Set([
    "marginTop",
    "marginRight",
    "marginBottom",
    "marginLeft",
    "paddingTop",
    "paddingRight",
    "paddingBottom",
    "paddingLeft",
    "width",
    "height",
    "maxWidth",
    "maxHeight",
    "minWidth",
    "minHeight",
    "widthStyle",
    "heightStyle",
    "maxWidthStyle",
    "maxHeightStyle",
    "minWidthStyle",
    "minHeightStyle",
    "alignItems",
    "textAlign",
    "display"
  ]),
  text: new Set([
    "fontFamily",
    "fontWeight",
    "fontStyle",
    "fontSize",
    "fontSizeStyle",
    "color",
    "lineHeight",
    "letterSpacing",
    "lineHeightStyle",
    "letterSpacingStyle",
    "textDecoration",
    "textTransform"
  ]),
  fill: new Set(["color", "backgroundImage", "repeatSwitch", "bgImageSize", "stretch", "imageFit", "bgImagePosition"]),
  border: new Set([
    "borderColor",
    "borderTopWidth",
    "borderRightWidth",
    "borderBottomWidth",
    "borderLeftWidth",
    "borderTopLeftRadius",
    "borderTopRightRadius",
    "borderBottomRightRadius",
    "borderBottomLeftRadius",
    "borderStyle"
  ]),
  shadow: new Set(["color", "x", "y", "b", "s", "isInset"]),
  effects: new Set([
    "opacity",
    "cursor",
    "pointerEvents",
    "mixBlendMode",
    "outlineStyle",
    "outlineWidth",
    "outlineColor",
    "outlineOffset",
    // Faz 3: multi-item arrays + Transform Settings scalars
    "transforms",
    "transitions",
    "transformOriginX",
    "transformOriginY",
    "perspective",
    "childrenPerspective",
    "childrenPerspectiveOriginX",
    "childrenPerspectiveOriginY",
    "backfaceVisibility",
    "transformStyle",
    // Faz 4: filter chain + backdrop-filter
    "filters",
    "backdropFilters",
    // Looping animations — per-state (kept in sync with STATE_ALLOWED_PROPS in styling-window-helpers.ts).
    "animations"
  ])
};

const STATE_SUBKEYS: ReadonlySet<string> = new Set(["hoverStyles", "pressedStyles", "focusedStyles", "disabledStyles"]);

// Mirror of the defaults that `ensureSection` writes when a section is missing
// from `di.styleObj`. The diff-based lift below uses this to distinguish "the
// AI actually requested this property" from "ensureSection synthesized this on
// section creation". Without the filter, the first state-style write into a
// brand-new section leaks the entire defaults bag into `{state}Styles` (e.g.
// text.hoverStyles ends up with `fontSizeStyle:"px"` and `textDecoration:""`).
// Keep this aligned with the switch inside `ensureSection`.
const ENSURE_SECTION_DEFAULTS: Record<string, Record<string, unknown>> = {
  text: {
    typoId: null,
    fontSizeStyle: "px",
    lineHeightStyle: "",
    letterSpacingStyle: "",
    textDecoration: ""
  },
  fill: {
    fillId: null,
    isVisible: false,
    repeatSwitch: false,
    bgImageSize: false,
    stretch: false,
    bgImagePosition: "left"
  },
  position: {
    positionId: null,
    widthStyle: "px",
    heightStyle: "px",
    maxWidthStyle: "px",
    maxHeightStyle: "px",
    minWidthStyle: "px",
    minHeightStyle: "px"
  },
  border: {
    borderId: null,
    isVisible: false,
    isBorderWidthLocked: true,
    isBorderRadiusLocked: true,
    borderStyle: "solid"
  },
  shadow: {
    shadowId: null,
    isVisible: false
  },
  effects: {
    effectsId: null
  }
};

interface StateLiftResult {
  liftedProperties: string[];
  unsupportedProperties: string[];
}

/**
 * Lifts the changes the apply loop made on base section properties into the
 * matching `{state}Styles` sub-object, then restores the original base values.
 * Anything outside STATE_ALLOWED_PROPS_BY_SECTION is reverted to base and
 * reported as unsupported so callers can warn the user.
 *
 * Known limitation: when an AI request asks for a state value that equals the
 * current base value, the diff sees no change and the corresponding entry on
 * `{state}Styles` is left untouched. If a stale state value is already there,
 * it survives. Per-property write tracking would close this gap; the current
 * apply loop is too sprawling (gradient/shorthand/border families branch into
 * many handlers) to instrument cheaply. The workaround until then is to set a
 * differently-encoded equivalent value (e.g. `#fff` vs `rgb(255,255,255)`) or
 * clear the state via a dedicated state-management op.
 */
function liftSectionChangesToState(
  styleObj: Record<string, unknown>,
  originalStyleObj: Record<string, unknown>,
  state: StyleState,
  touchedSections: Set<StyleSection>
): StateLiftResult {
  const stateKey = STATE_KEY_MAP[state];
  const liftedProperties: string[] = [];
  const unsupportedProperties: string[] = [];

  for (const section of touchedSections) {
    const newSection = styleObj[section] as Record<string, unknown> | undefined;
    if (!newSection) continue;

    const allowed = STATE_ALLOWED_PROPS_BY_SECTION[section] ?? new Set<string>();
    const origSection = (originalStyleObj[section] ?? {}) as Record<string, unknown>;
    const ensureDefaults = ENSURE_SECTION_DEFAULTS[section] ?? {};

    if (!newSection[stateKey] || typeof newSection[stateKey] !== "object") {
      newSection[stateKey] = { ...((origSection[stateKey] as Record<string, unknown>) ?? {}) };
    }
    const stateObj = newSection[stateKey] as Record<string, unknown>;

    const baseKeys = Object.keys(newSection).filter((k) => !STATE_SUBKEYS.has(k));
    for (const prop of baseKeys) {
      const newVal = newSection[prop];
      const origVal = origSection[prop];

      // ensureSection synthesized this prop on a fresh section — it was never
      // part of the AI's request, so leave it on base and skip the lift.
      if (origVal === undefined && prop in ensureDefaults) {
        const defaultVal = ensureDefaults[prop];
        if (JSON.stringify(newVal) === JSON.stringify(defaultVal)) continue;
      }

      const isDifferent = JSON.stringify(newVal) !== JSON.stringify(origVal);
      if (!isDifferent) continue;

      if (allowed.has(prop)) {
        stateObj[prop] = newVal;
        liftedProperties.push(`${section}.${prop}`);
      } else {
        unsupportedProperties.push(`${section}.${prop}`);
      }

      if (origVal === undefined) {
        delete newSection[prop];
      } else {
        newSection[prop] = origVal;
      }
    }
  }

  return { liftedProperties, unsupportedProperties };
}

function setIfVisible(
  target: any,
  key: string,
  value: any,
  inVisibleStyles: string[] | undefined | null,
  propertyMap: Record<string, string>
): boolean {
  const invisiblePart = propertyMap[key];
  if (invisiblePart && !isStyleVisible(inVisibleStyles, invisiblePart)) {
    return false;
  }
  target[key] = value;
  return true;
}

export interface IGradientStop {
  color: string; // rgba or hex
  position: number; // 0-100
}

export interface IGradientInput {
  angle?: number; // 0-360, default 90
  stops: IGradientStop[];
}

export interface CssStyleInput {
  // Text
  fontSize?: number | string;
  fontWeight?: number | string;
  color?: string;
  fontFamily?: string;
  lineHeight?: number | string;
  letterSpacing?: number | string;
  textDecoration?: string;
  textTransform?: string;
  // Fill
  backgroundColor?: string;
  backgroundGradient?: IGradientInput;
  backgroundImage?: string;
  // Position / Layout
  paddingTop?: number | string;
  paddingRight?: number | string;
  paddingBottom?: number | string;
  paddingLeft?: number | string;
  padding?: number | string;
  width?: number | string;
  height?: number | string;
  minWidth?: number | string;
  minHeight?: number | string;
  maxWidth?: number | string;
  maxHeight?: number | string;
  textAlign?: string;
  alignItems?: string;
  justifyContent?: string;
  display?: string;
  // Border
  border?: string; // CSS shorthand: "1px solid #color"
  borderTop?: string; // CSS shorthand per-side: "4px solid #FFF"
  borderRight?: string;
  borderBottom?: string;
  borderLeft?: string;
  borderRadius?: number | string;
  borderWidth?: number | string;
  borderColor?: string;
  borderStyle?: string;
  // Shadow
  shadowX?: number | string;
  shadowY?: number | string;
  shadowBlur?: number | string;
  shadowSpread?: number | string;
  shadowColor?: string;
  shadowInset?: boolean | string;
  // Effects
  opacity?: number | string;
  cursor?: string;
  pointerEvents?: string;
  mixBlendMode?: string;
  outlineStyle?: string;
  outlineWidth?: number | string;
  outlineColor?: string;
  outlineOffset?: number | string;
  // Effects — multi-item CSS strings parsed into structured arrays (web-only).
  // A leading `perspective(...)` inside `transform` is lifted to effects.perspective.
  transition?: string;
  transform?: string;
  filter?: string;
  backdropFilter?: string;
}

// --- Gradient helpers ---

function buildGradientCss(gradient: IGradientInput): string {
  const angle = gradient.angle ?? 90;
  const stops = [...gradient.stops].sort((a, b) => a.position - b.position);
  const stopStrings = stops.map((s) => `${s.color} ${s.position}%`);
  return `linear-gradient(${angle}deg, ${stopStrings.join(", ")})`;
}

function parseGradientCss(css: string): { angle: number; stops: IGradientStop[] } | null {
  if (!css || !css.trim().startsWith("linear-gradient")) return null;
  const angleMatch = css.match(/linear-gradient\((\d+)deg/);
  const angle = angleMatch ? parseInt(angleMatch[1], 10) : 90;
  const colorMatches = [...css.matchAll(/(#[0-9a-fA-F]{3,8}|rgba?\([^)]+\))/g)];
  const percentMatches = [...css.matchAll(/(\d+)%/g)];
  const stops: IGradientStop[] = [];
  for (let i = 0; i < Math.min(colorMatches.length, percentMatches.length); i++) {
    stops.push({
      color: colorMatches[i][0],
      position: parseInt(percentMatches[i][1], 10)
    });
  }
  if (stops.length === 0) return null;
  return { angle, stops };
}

// --- Enum mappings matching kuika-cl-model enums ---
// AlignItemsTypes: FlexStart=1, Center=2, FlexEnd=3
const ALIGN_ITEMS_MAP: Record<string, number> = {
  "flex-start": 1,
  start: 1,
  center: 2,
  "flex-end": 3,
  end: 3
};

// TextAlignTypes: Left=1, Center=2, Right=3
const TEXT_ALIGN_MAP: Record<string, number> = {
  left: 1,
  center: 2,
  right: 3
};

// DisplayTypes: InlineBlock=1, InlineFlex=2, Grid=3, Block=4, Inline=5, None=6
const DISPLAY_MAP: Record<string, number> = {
  "inline-block": 1,
  "inline-flex": 2,
  grid: 3,
  block: 4,
  inline: 5,
  none: 6
};

/**
 * Parse a dimension value like "100px", "50%", 100 into { value: number, style: "px"|"%"|"pt" }.
 * If a plain number, defaults to "px".
 */
function parseDimension(input: number | string): { value: number; style: string } | null {
  if (typeof input === "number") {
    return { value: input, style: "px" };
  }
  const str = String(input).trim();
  const match = str.match(/^(-?\d+(?:\.\d+)?)\s*(px|%|pt|vh|vw|em|rem)?$/);
  if (match) {
    return { value: parseFloat(match[1]), style: match[2] || "px" };
  }
  return null;
}

/**
 * Parse a CSS boxShadow string into decomposed shadow values.
 * Supports: "Xpx Ypx Bpx [Spx] color" with optional px units.
 * Returns null if unparseable.
 */
function parseBoxShadow(
  value: string
): { x: number; y: number; b: number; s?: number; color?: string; isInset?: boolean } | null {
  let str = String(value).trim();
  // `inset` is valid at the start or end of a box-shadow value. Strip it
  // (case-insensitive) before the anchored numeric/color match — otherwise the
  // keyword breaks every `^…$` pattern below and the whole value parses to null.
  let isInset = false;
  if (/^inset\b/i.test(str) || /\binset$/i.test(str)) {
    isInset = true;
    str = str
      .replace(/^inset\s*/i, "")
      .replace(/\s*inset$/i, "")
      .trim();
  }
  const numPat = "(-?\\d+(?:\\.\\d+)?(?:px|pt|em|rem)?)";
  const colorPat = "(#[0-9a-fA-F]{3,8}|rgba?\\([^)]+\\)|hsla?\\([^)]+\\)|[a-zA-Z]+)";

  // 4-value: x y blur spread color
  const re4 = new RegExp(`^${numPat}\\s+${numPat}\\s+${numPat}\\s+${numPat}\\s+${colorPat}$`);
  // 3-value: x y blur color (no spread)
  const re3 = new RegExp(`^${numPat}\\s+${numPat}\\s+${numPat}\\s+${colorPat}$`);
  // 3-value numeric only: x y blur (no color, no spread)
  const re3n = new RegExp(`^${numPat}\\s+${numPat}\\s+${numPat}$`);

  let parsed: { x: number; y: number; b: number; s?: number; color?: string } | null = null;
  let m = str.match(re4);
  if (m) {
    parsed = {
      x: parseNumericStyle(m[1]) ?? 0,
      y: parseNumericStyle(m[2]) ?? 0,
      b: parseNumericStyle(m[3]) ?? 0,
      s: parseNumericStyle(m[4]) ?? 0,
      color: m[5]
    };
  } else if ((m = str.match(re3))) {
    parsed = {
      x: parseNumericStyle(m[1]) ?? 0,
      y: parseNumericStyle(m[2]) ?? 0,
      b: parseNumericStyle(m[3]) ?? 0,
      color: m[4]
    };
  } else if ((m = str.match(re3n))) {
    parsed = {
      x: parseNumericStyle(m[1]) ?? 0,
      y: parseNumericStyle(m[2]) ?? 0,
      b: parseNumericStyle(m[3]) ?? 0
    };
  }

  if (!parsed) return null;
  return isInset ? { ...parsed, isInset: true } : parsed;
}

/**
 * Coerce a numeric style value that may arrive as a string (e.g. "12px", "16") to a plain number.
 * Returns null for unparseable or empty values so callers can skip assignment.
 */
function parseNumericStyle(input: number | string | undefined | null): number | null {
  if (input == null) return null;
  if (typeof input === "number") return isNaN(input) ? null : input;
  const str = String(input).trim();
  if (str === "") return null;
  const match = str.match(/^(-?\d+(?:\.\d+)?)\s*(?:px|pt|em|rem|%|vw|vh)?$/);
  return match ? parseFloat(match[1]) : null;
}

function isExplicitClearValue(input: unknown): boolean {
  return input === null || (typeof input === "string" && input.trim() === "");
}

/**
 * Parse CSS shorthand padding values:
 *  - "8px"          → all sides 8
 *  - "8px 12px"     → top/bottom 8, left/right 12
 *  - "8px 12px 8px 12px" → top 8, right 12, bottom 8, left 12
 */
function parseShorthandPadding(
  input: number | string | undefined | null
): { top: number; right: number; bottom: number; left: number } | null {
  if (input == null) return null;
  if (typeof input === "number") return isNaN(input) ? null : { top: input, right: input, bottom: input, left: input };
  const str = String(input).trim();
  if (str === "") return null;

  // Split by whitespace into parts
  const parts = str.split(/\s+/).map((p) => parseNumericStyle(p));
  if (parts.some((p) => p == null)) return null;

  const values = parts as number[];
  if (values.length === 1) {
    return { top: values[0], right: values[0], bottom: values[0], left: values[0] };
  }
  if (values.length === 2) {
    return { top: values[0], right: values[1], bottom: values[0], left: values[1] };
  }
  if (values.length === 3) {
    return { top: values[0], right: values[1], bottom: values[2], left: values[1] };
  }
  if (values.length === 4) {
    return { top: values[0], right: values[1], bottom: values[2], left: values[3] };
  }
  return null;
}

/**
 * Parse CSS shorthand border values:
 *  - "1px solid #333"       → { width: 1, style: "solid", color: "#333" }
 *  - "2px dashed rgba(…)"   → { width: 2, style: "dashed", color: "rgba(…)" }
 *  - "1px solid"            → { width: 1, style: "solid", color: undefined }
 *  - "none"                 → { width: 0, style: "none", color: undefined }
 */
function parseShorthandBorder(
  input: string | undefined | null
): { width: number; style: string; color: string | undefined } | null {
  if (input == null) return null;
  const str = String(input).trim();
  if (str === "") return null;

  if (str === "none") return { width: 0, style: "none", color: undefined };

  // Match: <width> <style> [<color>]
  // Color can be a hex, rgb(), rgba(), hsl(), hsla(), or named color
  const match = str.match(
    /^(\d+(?:\.\d+)?)\s*(?:px)?\s+(solid|dashed|dotted|double|groove|ridge|inset|outset|none)\s*(.*)?$/i
  );
  if (!match) return null;

  const width = parseFloat(match[1]);
  if (isNaN(width)) return null;
  const style = match[2].toLowerCase();
  const color = match[3]?.trim() || undefined;

  return { width, style, color: color && color.length > 0 ? color : undefined };
}

const FONT_WEIGHT_KEYWORDS: Record<string, number> = {
  thin: 100,
  hairline: 100,
  extralight: 200,
  ultralight: 200,
  light: 300,
  normal: 400,
  regular: 400,
  medium: 500,
  semibold: 600,
  demibold: 600,
  bold: 700,
  extrabold: 800,
  ultrabold: 800,
  black: 900,
  heavy: 900
};

export function parseFontWeight(input: number | string | undefined | null): number | null {
  if (input == null) return null;
  if (typeof input === "number") return input >= 100 && input <= 900 ? input : null;
  const str = String(input).trim();
  if (str === "") return null;
  const numeric = parseNumericStyle(input);
  if (numeric != null && numeric >= 100 && numeric <= 900) return numeric;
  const keyword = str.toLowerCase().replace(/[\s\-_]/g, "");
  return FONT_WEIGHT_KEYWORDS[keyword] ?? null;
}

function ensureSection(styleObj: any, section: string): any {
  if (!styleObj[section]) {
    // Initialize with proper defaults matching StyleHelper.setEmptyFieldsInStyleObj
    switch (section) {
      case "text":
        styleObj[section] = {
          typoId: null,
          fontSizeStyle: "px",
          lineHeightStyle: "",
          letterSpacingStyle: "",
          textDecoration: ""
        };
        break;
      case "fill":
        styleObj[section] = {
          fillId: null,
          isVisible: false,
          repeatSwitch: false,
          bgImageSize: false,
          stretch: false,
          bgImagePosition: "left"
        };
        break;
      case "position":
        styleObj[section] = {
          positionId: null,
          widthStyle: "px",
          heightStyle: "px",
          maxWidthStyle: "px",
          maxHeightStyle: "px",
          minWidthStyle: "px",
          minHeightStyle: "px"
        };
        break;
      case "border":
        styleObj[section] = {
          borderId: null,
          isVisible: false,
          isBorderWidthLocked: true,
          isBorderRadiusLocked: true,
          borderStyle: "solid"
        };
        break;
      case "shadow":
        styleObj[section] = { shadowId: null, isVisible: false };
        break;
      case "effects":
        styleObj[section] = { effectsId: null };
        break;
      default:
        styleObj[section] = {};
    }
  }
  return styleObj[section];
}

/**
 * Detects and flattens the sectioned format returned by get_component_styles
 * (e.g. { text: { fontSize: 16 }, fill: { backgroundColor: "#fff" } })
 * into the flat CssStyleInput format (e.g. { fontSize: 16, backgroundColor: "#fff" }).
 */
const SECTION_KEYS = new Set(["text", "fill", "position", "border", "shadow", "effects"]);

// Body-root Row/Col are the responsive grid skeleton. Layout-affecting styles
// would tear that skeleton apart, so they remain blocked. Fill (backgroundColor /
// backgroundGradient) is safe — it paints the cell without changing its grid
// contract — so we allow it through. Previously the guard allow-listed
// alignment-only, which made "set this column's background to red" bounce off
// as a silent skip on body-root Cols.
export const STRUCTURAL_WRAPPER_ALLOWED_STYLE_KEYS = new Set([
  "alignItems",
  "textAlign",
  "justifyContent",
  "backgroundColor",
  "backgroundGradient"
]);

export function flattenSectionedStyles(styles: any): CssStyleInput {
  const hasSection = Object.keys(styles).some((k) => SECTION_KEYS.has(k) && typeof styles[k] === "object");
  if (!hasSection) return styles;

  const flat: Record<string, any> = {};
  for (const [key, value] of Object.entries(styles)) {
    if (SECTION_KEYS.has(key) && typeof value === "object" && value !== null) {
      Object.assign(flat, value);
    } else {
      flat[key] = value;
    }
  }
  return flat as CssStyleInput;
}

export function canApplyStylesToStructuralWrapper(styles: unknown): boolean {
  if (!styles || typeof styles !== "object") return false;
  const flatStyles = flattenSectionedStyles(styles);
  const styleKeys = Object.keys(flatStyles);
  return styleKeys.length > 0 && styleKeys.every((key) => STRUCTURAL_WRAPPER_ALLOWED_STYLE_KEYS.has(key));
}

const JUSTIFY_CONTENT_TO_TEXT_ALIGN_MAP: Record<string, string> = {
  "flex-start": "left",
  start: "left",
  left: "left",
  center: "center",
  "flex-end": "right",
  end: "right",
  right: "right"
};

export function applyCssStylesToComponent(
  sd: ScreenDesignInstance,
  di: IDisplayItem,
  kuikaId: string,
  styles: CssStyleInput,
  options?: { shouldCommit?: boolean; state?: StyleState }
): { success: boolean; appliedProperties: string[]; skippedProperties: string[]; error?: string } {
  styles = flattenSectionedStyles(styles);
  const originalStyleObjSnapshot = deepClone(di.styleObj || {}) as Record<string, unknown>;

  const componentDef = getComponentDef(sd, di);
  const inVisibleStyles: string[] | undefined = componentDef?.inVisibleStyles;

  // AI tool override: always allow these style sections even if the
  // component definition hides them in `inVisibleStyles`. The blocklist
  // exists to keep the manual property panel UI uncluttered — these
  // properties ARE functional on every component (the dimension/display
  // handlers below parse and store them in the position section, the
  // border handlers store them in the border section, etc.) — they're
  // just hidden from the human-facing UI to reduce noise.
  //
  // The AI tool needs to bypass the UI-level hiding because:
  //   - `layout_padding`: chip/badge styling (backgroundColor + padding)
  //   - `layout_display`/`width`/`height`: layout corrections, including
  //     the "make Button full-width" case that previously trapped the
  //     LLM in a retry loop because `display:block` and `width:100%`
  //     were silently dropped, returning ok:0 with no errors. Now the
  //     LLM's intent applies as written. Note: the canonical Kuika way
  //     to make a leaf component fill its column is still to set the
  //     parent Col's responsive xs/sm/md/lg/xl/xxl=24, but the explicit
  //     CSS form must also work because that's what the LLM reaches for
  //     first based on its training data.
  const AI_OVERRIDE_VISIBLE = new Set([
    "layout_padding",
    "layout_alignItems",
    "layout_textAlign",
    "layout_display",
    "layout_width",
    "layout_height",
    "layout_minWidth",
    "layout_minHeight",
    "layout_maxWidth",
    "layout_maxHeight",
    "border",
    "border_borderWidth",
    "border_borderColor",
    "border_borderStyle",
    "border_borderRadius",
    // KOA-16241: theme-flip surface. Without these overrides the AI can
    // call ui_style.set on a real kuikaId and get "success:true, count:0"
    // because the component's componentDef.inVisibleStyles hides fill /
    // text / shadow to keep the manual property panel clean. The style
    // merge itself works perfectly on every component type; the hide was
    // UI-layer noise reduction, not a semantic restriction. The AI needs
    // these to flip backgrounds, text colors, and shadows during
    // light↔dark mode conversions — blocking them silently was the root
    // cause of the "only body changes, panels stay dark" bug.
    "fill",
    "fill_color",
    "fill_backgroundImage",
    "text",
    "text_color",
    "text_fontSize",
    "text_fontWeight",
    "text_fontFamily",
    "text_lineHeight",
    "text_letterSpacing",
    "text_textDecoration",
    "text_textTransform",
    "shadow"
  ]);
  const effectiveInVisibleStyles = inVisibleStyles?.filter((s) => !AI_OVERRIDE_VISIBLE.has(s));

  const styleObj = deepClone(di.styleObj || {}) as Record<string, unknown>;
  const appliedProperties: string[] = [];
  const skippedProperties: string[] = [];
  const touchedSections = new Set<StyleSection>();
  const isColLike = di.item?.name === "Col" || di.item?.name === "Column";
  const touchSection = (section: StyleSection): void => {
    touchedSections.add(section);
  };

  if (styles.justifyContent !== undefined) {
    const mappedTextAlign = JUSTIFY_CONTENT_TO_TEXT_ALIGN_MAP[String(styles.justifyContent).toLowerCase()];
    if (isColLike && mappedTextAlign) {
      if (styles.textAlign === undefined) {
        styles.textAlign = mappedTextAlign;
      }
      appliedProperties.push("justifyContent");
    } else {
      skippedProperties.push("justifyContent");
    }
    delete styles.justifyContent;
  }

  // Helper to check if a section+property is visible
  const canApply = (section: string, property?: string): boolean => {
    if (!isStyleVisible(effectiveInVisibleStyles, section)) return false;
    if (property && !isStyleVisible(effectiveInVisibleStyles, property)) return false;
    return true;
  };

  // --- Text section ---
  if (styles.fontSize !== undefined) {
    if (canApply("text", "text_fontSize")) {
      if (isExplicitClearValue(styles.fontSize)) {
        const txt = ensureSection(styleObj, "text");
        txt.fontSize = null;
        txt.fontSizeStyle = "px";
        appliedProperties.push("fontSize");
        touchSection("text");
      } else {
        const v = parseNumericStyle(styles.fontSize);
        if (v != null) {
          const txt = ensureSection(styleObj, "text");
          txt.fontSize = v;
          txt.fontSizeStyle = "px";
          appliedProperties.push("fontSize");
          touchSection("text");
        } else {
          skippedProperties.push("fontSize");
        }
      }
    } else {
      skippedProperties.push("fontSize");
    }
  }

  if (styles.fontWeight !== undefined) {
    if (canApply("text", "text_fontWeight")) {
      if (isExplicitClearValue(styles.fontWeight)) {
        ensureSection(styleObj, "text").fontWeight = null;
        appliedProperties.push("fontWeight");
        touchSection("text");
      } else {
        const v = parseFontWeight(styles.fontWeight);
        if (v != null) {
          ensureSection(styleObj, "text").fontWeight = v;
          appliedProperties.push("fontWeight");
          touchSection("text");
        } else {
          skippedProperties.push(
            `fontWeight (invalid value: "${styles.fontWeight}". Use a number 100-900, e.g. 400=normal, 700=bold)`
          );
        }
      }
    } else {
      skippedProperties.push("fontWeight");
    }
  }

  if (styles.color !== undefined) {
    if (canApply("text", "text_color")) {
      ensureSection(styleObj, "text").color = isExplicitClearValue(styles.color) ? null : styles.color;
      appliedProperties.push("color");
      touchSection("text");
    } else {
      skippedProperties.push("color");
    }
  }

  if (styles.fontFamily !== undefined) {
    if (canApply("text", "text_fontFamily")) {
      ensureSection(styleObj, "text").fontFamily = isExplicitClearValue(styles.fontFamily) ? null : styles.fontFamily;
      appliedProperties.push("fontFamily");
      touchSection("text");
    } else {
      skippedProperties.push("fontFamily");
    }
  }

  if (styles.lineHeight !== undefined) {
    if (canApply("text", "text_lineHeight")) {
      if (isExplicitClearValue(styles.lineHeight)) {
        const txt = ensureSection(styleObj, "text");
        txt.lineHeight = null;
        txt.lineHeightStyle = "px";
        appliedProperties.push("lineHeight");
        touchSection("text");
      } else {
        const v = parseNumericStyle(styles.lineHeight);
        if (v != null) {
          const txt = ensureSection(styleObj, "text");
          txt.lineHeight = v;
          txt.lineHeightStyle = "px";
          appliedProperties.push("lineHeight");
          touchSection("text");
        } else {
          skippedProperties.push("lineHeight");
        }
      }
    } else {
      skippedProperties.push("lineHeight");
    }
  }

  if (styles.letterSpacing !== undefined) {
    if (canApply("text", "text_letterSpacing")) {
      if (isExplicitClearValue(styles.letterSpacing)) {
        const txt = ensureSection(styleObj, "text");
        txt.letterSpacing = null;
        txt.letterSpacingStyle = "px";
        appliedProperties.push("letterSpacing");
        touchSection("text");
      } else {
        const v = parseNumericStyle(styles.letterSpacing);
        if (v != null) {
          const txt = ensureSection(styleObj, "text");
          txt.letterSpacing = v;
          txt.letterSpacingStyle = "px";
          appliedProperties.push("letterSpacing");
          touchSection("text");
        } else {
          skippedProperties.push("letterSpacing");
        }
      }
    } else {
      skippedProperties.push("letterSpacing");
    }
  }

  if (styles.textDecoration !== undefined) {
    if (canApply("text", "text_textDecoration")) {
      ensureSection(styleObj, "text").textDecoration = isExplicitClearValue(styles.textDecoration)
        ? null
        : styles.textDecoration;
      appliedProperties.push("textDecoration");
      touchSection("text");
    } else {
      skippedProperties.push("textDecoration");
    }
  }

  if (styles.textTransform !== undefined) {
    if (canApply("text", "text_textTransform")) {
      ensureSection(styleObj, "text").textTransform = isExplicitClearValue(styles.textTransform)
        ? null
        : styles.textTransform;
      appliedProperties.push("textTransform");
      touchSection("text");
    } else {
      skippedProperties.push("textTransform");
    }
  }

  // --- Fill section ---
  // Auto-remap: if backgroundColor contains a gradient string, parse and reroute
  let effectiveBackgroundGradient = styles.backgroundGradient;
  let effectiveBackgroundColor = styles.backgroundColor;
  if (
    !effectiveBackgroundGradient &&
    effectiveBackgroundColor &&
    typeof effectiveBackgroundColor === "string" &&
    isGradient(effectiveBackgroundColor)
  ) {
    // parseGradientCss only succeeds for linear, so only linear maps to the
    // structural backgroundGradient editor. Radial/conic return null here and
    // stay in effectiveBackgroundColor, applied verbatim to fill.color below
    // (StyleHelper emits them as background-image at render).
    const parsed = parseGradientCss(effectiveBackgroundColor);
    if (parsed) {
      effectiveBackgroundGradient = { angle: parsed.angle, stops: parsed.stops };
      effectiveBackgroundColor = undefined;
    }
  }

  if (effectiveBackgroundGradient !== undefined) {
    if (canApply("fill", "fill_color")) {
      if (isExplicitClearValue(effectiveBackgroundGradient)) {
        ensureSection(styleObj, "fill").color = null;
        appliedProperties.push("backgroundGradient");
        touchSection("fill");
      } else {
        const grad = effectiveBackgroundGradient;
        if (grad && Array.isArray(grad.stops) && grad.stops.length >= 2) {
          const cssGradient = buildGradientCss(grad);
          ensureSection(styleObj, "fill").color = cssGradient;
          appliedProperties.push(
            styles.backgroundGradient !== undefined
              ? "backgroundGradient"
              : "backgroundGradient (auto-remapped from backgroundColor)"
          );
          touchSection("fill");
        } else {
          skippedProperties.push("backgroundGradient (invalid: needs at least 2 stops)");
        }
      }
    } else {
      skippedProperties.push("backgroundGradient");
    }
  } else if (effectiveBackgroundColor !== undefined) {
    if (canApply("fill", "fill_color")) {
      ensureSection(styleObj, "fill").color = isExplicitClearValue(effectiveBackgroundColor)
        ? null
        : effectiveBackgroundColor;
      appliedProperties.push("backgroundColor");
      touchSection("fill");
    } else {
      skippedProperties.push("backgroundColor");
    }
  }

  if (styles.backgroundImage !== undefined) {
    if (canApply("fill", "fill_backgroundImage")) {
      ensureSection(styleObj, "fill").backgroundImage = isExplicitClearValue(styles.backgroundImage)
        ? null
        : styles.backgroundImage;
      appliedProperties.push("backgroundImage");
      touchSection("fill");
    } else {
      skippedProperties.push("backgroundImage");
    }
  }

  // --- Position / Layout section ---
  // Shorthand padding — supports single value ("8px"), 2-value ("8px 12px"), and 4-value ("8px 12px 8px 12px")
  if (styles.padding !== undefined) {
    if (canApply("layout", "layout_padding")) {
      if (isExplicitClearValue(styles.padding)) {
        const pos = ensureSection(styleObj, "position");
        pos.paddingTop = null;
        pos.paddingRight = null;
        pos.paddingBottom = null;
        pos.paddingLeft = null;
        pos.isPaddingLocked = true;
        appliedProperties.push("padding");
        touchSection("position");
      } else {
        const paddingParsed = parseShorthandPadding(styles.padding);
        if (paddingParsed) {
          const pos = ensureSection(styleObj, "position");
          pos.paddingTop = paddingParsed.top;
          pos.paddingRight = paddingParsed.right;
          pos.paddingBottom = paddingParsed.bottom;
          pos.paddingLeft = paddingParsed.left;
          pos.isPaddingLocked =
            paddingParsed.top === paddingParsed.right &&
            paddingParsed.right === paddingParsed.bottom &&
            paddingParsed.bottom === paddingParsed.left;
          appliedProperties.push("padding");
          touchSection("position");
        } else {
          skippedProperties.push("padding");
        }
      }
    } else {
      skippedProperties.push("padding");
    }
  }

  // Individual paddings (override shorthand if both specified)
  const paddingFields: Array<[keyof CssStyleInput, string]> = [
    ["paddingTop", "paddingTop"],
    ["paddingRight", "paddingRight"],
    ["paddingBottom", "paddingBottom"],
    ["paddingLeft", "paddingLeft"]
  ];
  for (const [inputKey, posKey] of paddingFields) {
    if (styles[inputKey] !== undefined) {
      if (canApply("layout", "layout_padding")) {
        if (isExplicitClearValue(styles[inputKey])) {
          ensureSection(styleObj, "position")[posKey] = null;
          appliedProperties.push(inputKey as string);
          touchSection("position");
        } else {
          const v = parseNumericStyle(styles[inputKey] as number | string);
          if (v != null) {
            ensureSection(styleObj, "position")[posKey] = v;
            appliedProperties.push(inputKey as string);
            touchSection("position");
          } else {
            skippedProperties.push(inputKey as string);
          }
        }
      } else {
        skippedProperties.push(inputKey as string);
      }
    }
  }

  // Dimension properties: stored as number + separate *Style field
  const dimensionFields: Array<[keyof CssStyleInput, string, string, string]> = [
    ["width", "width", "widthStyle", "layout_width"],
    ["height", "height", "heightStyle", "layout_height"],
    ["minWidth", "minWidth", "minWidthStyle", "layout_minWidth"],
    ["minHeight", "minHeight", "minHeightStyle", "layout_minHeight"],
    ["maxWidth", "maxWidth", "maxWidthStyle", "layout_maxWidth"],
    ["maxHeight", "maxHeight", "maxHeightStyle", "layout_maxHeight"]
  ];

  for (const [inputKey, valueField, styleField, invisibleKey] of dimensionFields) {
    if (styles[inputKey] !== undefined) {
      if (canApply("layout", invisibleKey)) {
        const rawVal = styles[inputKey];
        // Allow clearing dimension by setting to "auto", "", null, or "none"
        if (rawVal === "auto" || rawVal === "" || rawVal === null || rawVal === "none") {
          const pos = ensureSection(styleObj, "position");
          pos[valueField] = null;
          pos[styleField] = "px";
          appliedProperties.push(inputKey as string);
          touchSection("position");
        } else {
          const parsed = parseDimension(rawVal as number | string);
          if (parsed) {
            const pos = ensureSection(styleObj, "position");
            pos[valueField] = parsed.value;
            pos[styleField] = parsed.style;
            appliedProperties.push(inputKey as string);
            touchSection("position");
          } else {
            skippedProperties.push(
              `${inputKey as string} (invalid value: "${rawVal}". Use a number, or number with unit: px, %, pt, vh, vw, em, rem)`
            );
          }
        }
      } else {
        skippedProperties.push(inputKey as string);
      }
    }
  }

  // textAlign → enum: Left=1, Center=2, Right=3
  if (styles.textAlign !== undefined) {
    if (canApply("layout", "layout_textAlign")) {
      if (isExplicitClearValue(styles.textAlign)) {
        ensureSection(styleObj, "position").textAlign = null;
        appliedProperties.push("textAlign");
        touchSection("position");
      } else {
        const enumVal = TEXT_ALIGN_MAP[styles.textAlign.toLowerCase()];
        if (enumVal !== undefined) {
          ensureSection(styleObj, "position").textAlign = enumVal;
          appliedProperties.push("textAlign");
          touchSection("position");
        } else {
          skippedProperties.push("textAlign");
        }
      }
    } else {
      skippedProperties.push("textAlign");
    }
  }

  // alignItems → enum: FlexStart=1, Center=2, FlexEnd=3
  if (styles.alignItems !== undefined) {
    if (canApply("layout", "layout_alignItems")) {
      if (isExplicitClearValue(styles.alignItems)) {
        ensureSection(styleObj, "position").alignItems = null;
        appliedProperties.push("alignItems");
        touchSection("position");
      } else {
        const enumVal = ALIGN_ITEMS_MAP[styles.alignItems.toLowerCase()];
        if (enumVal !== undefined) {
          ensureSection(styleObj, "position").alignItems = enumVal;
          appliedProperties.push("alignItems");
          touchSection("position");
        } else {
          skippedProperties.push("alignItems");
        }
      }
    } else {
      skippedProperties.push("alignItems");
    }
  }

  // display → enum: InlineBlock=1, InlineFlex=2, Grid=3, Block=4, Inline=5, None=6
  if (styles.display !== undefined) {
    if (canApply("layout", "layout_display")) {
      if (isExplicitClearValue(styles.display)) {
        ensureSection(styleObj, "position").display = null;
        appliedProperties.push("display");
        touchSection("position");
      } else {
        const enumVal = DISPLAY_MAP[styles.display.toLowerCase()];
        if (enumVal !== undefined) {
          ensureSection(styleObj, "position").display = enumVal;
          appliedProperties.push("display");
          touchSection("position");
        } else {
          skippedProperties.push("display");
        }
      }
    } else {
      skippedProperties.push("display");
    }
  }

  // --- Border section ---
  // Shorthand border — supports "1px solid #color", "2px dashed rgba(…)", "none"
  if (styles.border !== undefined) {
    if (isExplicitClearValue(styles.border)) {
      const border = ensureSection(styleObj, "border");
      border.borderTopWidth = null;
      border.borderRightWidth = null;
      border.borderBottomWidth = null;
      border.borderLeftWidth = null;
      border.borderStyle = null;
      border.borderColor = null;
      border.isBorderWidthLocked = true;
      appliedProperties.push("border");
      touchSection("border");
    } else {
      const borderParsed = parseShorthandBorder(styles.border);
      if (borderParsed) {
        if (canApply("border", "border_borderWidth")) {
          const border = ensureSection(styleObj, "border");
          border.borderTopWidth = borderParsed.width;
          border.borderRightWidth = borderParsed.width;
          border.borderBottomWidth = borderParsed.width;
          border.borderLeftWidth = borderParsed.width;
          border.isBorderWidthLocked = true;
        }
        if (canApply("border", "border_borderStyle")) {
          ensureSection(styleObj, "border").borderStyle = borderParsed.style;
        }
        if (borderParsed.color && canApply("border", "border_borderColor")) {
          ensureSection(styleObj, "border").borderColor = borderParsed.color;
        }
        appliedProperties.push("border");
        touchSection("border");
      } else {
        skippedProperties.push("border");
      }
    }
  }

  // Per-side border shorthands: borderTop, borderRight, borderBottom, borderLeft
  const borderSides: Array<{
    prop: keyof CssStyleInput;
    widthKey: string;
  }> = [
    { prop: "borderTop", widthKey: "borderTopWidth" },
    { prop: "borderRight", widthKey: "borderRightWidth" },
    { prop: "borderBottom", widthKey: "borderBottomWidth" },
    { prop: "borderLeft", widthKey: "borderLeftWidth" }
  ];
  for (const side of borderSides) {
    const val = styles[side.prop] as string | undefined;
    if (val !== undefined) {
      if (isExplicitClearValue(val)) {
        const border = ensureSection(styleObj, "border");
        (border as any)[side.widthKey] = null;
        border.isBorderWidthLocked = false;
        appliedProperties.push(side.prop as string);
        touchSection("border");
      } else {
        const parsed = parseShorthandBorder(val);
        if (parsed) {
          if (canApply("border", "border_borderWidth")) {
            const border = ensureSection(styleObj, "border");
            (border as any)[side.widthKey] = parsed.width;
            border.isBorderWidthLocked = false;
          }
          if (parsed.style && canApply("border", "border_borderStyle")) {
            ensureSection(styleObj, "border").borderStyle = parsed.style;
          }
          if (parsed.color && canApply("border", "border_borderColor")) {
            ensureSection(styleObj, "border").borderColor = parsed.color;
          }
          appliedProperties.push(side.prop as string);
          touchSection("border");
        } else {
          skippedProperties.push(side.prop as string);
        }
      }
    }
  }

  if (styles.borderRadius !== undefined) {
    if (canApply("border", "border_borderRadius")) {
      if (isExplicitClearValue(styles.borderRadius)) {
        const border = ensureSection(styleObj, "border");
        border.borderTopLeftRadius = null;
        border.borderTopRightRadius = null;
        border.borderBottomRightRadius = null;
        border.borderBottomLeftRadius = null;
        border.isBorderRadiusLocked = true;
        appliedProperties.push("borderRadius");
        touchSection("border");
      } else {
        const v = parseNumericStyle(styles.borderRadius);
        if (v != null) {
          const border = ensureSection(styleObj, "border");
          border.borderTopLeftRadius = v;
          border.borderTopRightRadius = v;
          border.borderBottomRightRadius = v;
          border.borderBottomLeftRadius = v;
          border.isBorderRadiusLocked = true;
          appliedProperties.push("borderRadius");
          touchSection("border");
        } else {
          skippedProperties.push("borderRadius");
        }
      }
    } else {
      skippedProperties.push("borderRadius");
    }
  }

  if (styles.borderWidth !== undefined) {
    if (canApply("border", "border_borderWidth")) {
      if (isExplicitClearValue(styles.borderWidth)) {
        const border = ensureSection(styleObj, "border");
        border.borderTopWidth = null;
        border.borderRightWidth = null;
        border.borderBottomWidth = null;
        border.borderLeftWidth = null;
        border.isBorderWidthLocked = true;
        appliedProperties.push("borderWidth");
        touchSection("border");
      } else {
        const v = parseNumericStyle(styles.borderWidth);
        if (v != null) {
          const border = ensureSection(styleObj, "border");
          border.borderTopWidth = v;
          border.borderRightWidth = v;
          border.borderBottomWidth = v;
          border.borderLeftWidth = v;
          border.isBorderWidthLocked = true;
          appliedProperties.push("borderWidth");
          touchSection("border");
        } else {
          skippedProperties.push("borderWidth");
        }
      }
    } else {
      skippedProperties.push("borderWidth");
    }
  }

  if (styles.borderColor !== undefined) {
    if (canApply("border", "border_borderColor")) {
      ensureSection(styleObj, "border").borderColor = isExplicitClearValue(styles.borderColor)
        ? null
        : styles.borderColor;
      appliedProperties.push("borderColor");
      touchSection("border");
    } else {
      skippedProperties.push("borderColor");
    }
  }

  if (styles.borderStyle !== undefined) {
    if (canApply("border", "border_borderStyle")) {
      ensureSection(styleObj, "border").borderStyle = isExplicitClearValue(styles.borderStyle)
        ? null
        : styles.borderStyle;
      appliedProperties.push("borderStyle");
      touchSection("border");
    } else {
      skippedProperties.push("borderStyle");
    }
  }

  // --- Normalize boxShadow CSS string into decomposed shadow properties ---
  if ((styles as any).boxShadow && typeof (styles as any).boxShadow === "string") {
    const parsed = parseBoxShadow((styles as any).boxShadow);
    if (parsed) {
      if (styles.shadowX === undefined) styles.shadowX = parsed.x;
      if (styles.shadowY === undefined) styles.shadowY = parsed.y;
      if (styles.shadowBlur === undefined) styles.shadowBlur = parsed.b;
      if (parsed.s !== undefined && styles.shadowSpread === undefined) styles.shadowSpread = parsed.s;
      if (parsed.color && styles.shadowColor === undefined) styles.shadowColor = parsed.color;
      if (parsed.isInset !== undefined && styles.shadowInset === undefined) styles.shadowInset = parsed.isInset;
    }
    delete (styles as any).boxShadow;
  }

  // --- Shadow section ---
  const hasShadowInput =
    styles.shadowX !== undefined ||
    styles.shadowY !== undefined ||
    styles.shadowBlur !== undefined ||
    styles.shadowSpread !== undefined ||
    styles.shadowColor !== undefined ||
    styles.shadowInset !== undefined;

  if (hasShadowInput) {
    if (!canApply("shadow")) {
      if (styles.shadowX !== undefined) skippedProperties.push("shadowX");
      if (styles.shadowY !== undefined) skippedProperties.push("shadowY");
      if (styles.shadowBlur !== undefined) skippedProperties.push("shadowBlur");
      if (styles.shadowSpread !== undefined) skippedProperties.push("shadowSpread");
      if (styles.shadowColor !== undefined) skippedProperties.push("shadowColor");
      if (styles.shadowInset !== undefined) skippedProperties.push("shadowInset");
    } else {
      const shadow = ensureSection(styleObj, "shadow");
      const canSetStructure = isStyleVisible(inVisibleStyles, "shadow_structure");
      const canSetColor = isStyleVisible(inVisibleStyles, "shadow_color");

      if (canSetStructure) {
        if (styles.shadowX !== undefined) {
          if (isExplicitClearValue(styles.shadowX)) {
            shadow.x = null;
            appliedProperties.push("shadowX");
            touchSection("shadow");
          } else {
            const v = parseNumericStyle(styles.shadowX);
            if (v != null) {
              shadow.x = v;
              appliedProperties.push("shadowX");
              touchSection("shadow");
            } else {
              skippedProperties.push("shadowX");
            }
          }
        }
        if (styles.shadowY !== undefined) {
          if (isExplicitClearValue(styles.shadowY)) {
            shadow.y = null;
            appliedProperties.push("shadowY");
            touchSection("shadow");
          } else {
            const v = parseNumericStyle(styles.shadowY);
            if (v != null) {
              shadow.y = v;
              appliedProperties.push("shadowY");
              touchSection("shadow");
            } else {
              skippedProperties.push("shadowY");
            }
          }
        }
        if (styles.shadowBlur !== undefined) {
          if (isExplicitClearValue(styles.shadowBlur)) {
            shadow.b = null;
            appliedProperties.push("shadowBlur");
            touchSection("shadow");
          } else {
            const v = parseNumericStyle(styles.shadowBlur);
            if (v != null) {
              shadow.b = v;
              appliedProperties.push("shadowBlur");
              touchSection("shadow");
            } else {
              skippedProperties.push("shadowBlur");
            }
          }
        }
        if (styles.shadowSpread !== undefined) {
          if (isExplicitClearValue(styles.shadowSpread)) {
            shadow.s = null;
            appliedProperties.push("shadowSpread");
            touchSection("shadow");
          } else {
            const v = parseNumericStyle(styles.shadowSpread);
            if (v != null) {
              shadow.s = v;
              appliedProperties.push("shadowSpread");
              touchSection("shadow");
            } else {
              skippedProperties.push("shadowSpread");
            }
          }
        }
        // `inset` is structural (the shadow's geometry, not its color), so it
        // is gated under canSetStructure alongside x/y/blur/spread. Both the
        // markup path (shadowInset:"true") and the raw-boxShadow path
        // (parsed.isInset:true) feed this; codegen reads shadow.isInset===true.
        if (styles.shadowInset !== undefined) {
          if (isExplicitClearValue(styles.shadowInset)) {
            shadow.isInset = null;
          } else {
            shadow.isInset = styles.shadowInset === true || styles.shadowInset === "true";
          }
          appliedProperties.push("shadowInset");
          touchSection("shadow");
        }
      } else {
        if (styles.shadowX !== undefined) skippedProperties.push("shadowX");
        if (styles.shadowY !== undefined) skippedProperties.push("shadowY");
        if (styles.shadowBlur !== undefined) skippedProperties.push("shadowBlur");
        if (styles.shadowSpread !== undefined) skippedProperties.push("shadowSpread");
        if (styles.shadowInset !== undefined) skippedProperties.push("shadowInset");
      }

      if (canSetColor && styles.shadowColor !== undefined) {
        shadow.color = isExplicitClearValue(styles.shadowColor) ? null : styles.shadowColor;
        appliedProperties.push("shadowColor");
        touchSection("shadow");
      } else if (styles.shadowColor !== undefined) {
        skippedProperties.push("shadowColor");
      }
    }
  }

  // --- Effects section ---
  // Single-value properties (opacity, cursor, pointer-events, mix-blend-mode)
  // and the 4 outline sub-fields all live under styleObj.effects. Visibility
  // gate is per-property via the effects_* keys documented at the
  // top-of-file Effects field → inVisibleStyles key reference.
  if (styles.opacity !== undefined) {
    if (canApply("effects", "effects_opacity")) {
      if (isExplicitClearValue(styles.opacity)) {
        ensureSection(styleObj, "effects").opacity = null;
        appliedProperties.push("opacity");
        touchSection("effects");
      } else {
        const v: number | null = parseNumericStyle(styles.opacity);
        if (v != null) {
          ensureSection(styleObj, "effects").opacity = v;
          appliedProperties.push("opacity");
          touchSection("effects");
        } else {
          skippedProperties.push("opacity");
        }
      }
    } else {
      skippedProperties.push("opacity");
    }
  }

  if (styles.cursor !== undefined) {
    if (canApply("effects", "effects_cursor")) {
      ensureSection(styleObj, "effects").cursor = isExplicitClearValue(styles.cursor) ? null : styles.cursor;
      appliedProperties.push("cursor");
      touchSection("effects");
    } else {
      skippedProperties.push("cursor");
    }
  }

  if (styles.pointerEvents !== undefined) {
    if (canApply("effects", "effects_pointerEvents")) {
      ensureSection(styleObj, "effects").pointerEvents = isExplicitClearValue(styles.pointerEvents)
        ? null
        : styles.pointerEvents;
      appliedProperties.push("pointerEvents");
      touchSection("effects");
    } else {
      skippedProperties.push("pointerEvents");
    }
  }

  if (styles.mixBlendMode !== undefined) {
    if (canApply("effects", "effects_blending")) {
      ensureSection(styleObj, "effects").mixBlendMode = isExplicitClearValue(styles.mixBlendMode)
        ? null
        : styles.mixBlendMode;
      appliedProperties.push("mixBlendMode");
      touchSection("effects");
    } else {
      skippedProperties.push("mixBlendMode");
    }
  }

  if (styles.outlineStyle !== undefined) {
    if (canApply("effects", "effects_outline")) {
      ensureSection(styleObj, "effects").outlineStyle = isExplicitClearValue(styles.outlineStyle)
        ? null
        : styles.outlineStyle;
      appliedProperties.push("outlineStyle");
      touchSection("effects");
    } else {
      skippedProperties.push("outlineStyle");
    }
  }

  if (styles.outlineWidth !== undefined) {
    if (canApply("effects", "effects_outline")) {
      if (isExplicitClearValue(styles.outlineWidth)) {
        ensureSection(styleObj, "effects").outlineWidth = null;
        appliedProperties.push("outlineWidth");
        touchSection("effects");
      } else {
        const v: number | null = parseNumericStyle(styles.outlineWidth);
        if (v != null) {
          ensureSection(styleObj, "effects").outlineWidth = v;
          appliedProperties.push("outlineWidth");
          touchSection("effects");
        } else {
          skippedProperties.push("outlineWidth");
        }
      }
    } else {
      skippedProperties.push("outlineWidth");
    }
  }

  if (styles.outlineColor !== undefined) {
    if (canApply("effects", "effects_outline")) {
      ensureSection(styleObj, "effects").outlineColor = isExplicitClearValue(styles.outlineColor)
        ? null
        : styles.outlineColor;
      appliedProperties.push("outlineColor");
      touchSection("effects");
    } else {
      skippedProperties.push("outlineColor");
    }
  }

  if (styles.outlineOffset !== undefined) {
    if (canApply("effects", "effects_outline")) {
      if (isExplicitClearValue(styles.outlineOffset)) {
        ensureSection(styleObj, "effects").outlineOffset = null;
        appliedProperties.push("outlineOffset");
        touchSection("effects");
      } else {
        const v: number | null = parseNumericStyle(styles.outlineOffset);
        if (v != null) {
          ensureSection(styleObj, "effects").outlineOffset = v;
          appliedProperties.push("outlineOffset");
          touchSection("effects");
        } else {
          skippedProperties.push("outlineOffset");
        }
      }
    } else {
      skippedProperties.push("outlineOffset");
    }
  }

  // Multi-item effect arrays (transition / transform / filter / backdrop-filter).
  // The MCP/AI path speaks raw CSS strings; StyleHelper.parse*Css (kuika-cl-model)
  // turns them into the structured item arrays the Effects panel stores — the
  // exact inverse of the canvas/codegen build*InlineStyle builders. Each item
  // gets a fresh uuid so a panel round-trip renders. These are web-only: native
  // generators never read styleObj.effects. State-mode routing is automatic —
  // the keys are in STATE_ALLOWED_PROPS_BY_SECTION.effects, so liftSectionChanges-
  // ToState moves them into {state}Styles below.
  if (styles.transition !== undefined) {
    if (canApply("effects", "effects_transitions")) {
      const eff = ensureSection(styleObj, "effects");
      eff.transitions = isExplicitClearValue(styles.transition)
        ? []
        : StyleHelper.parseTransitionCss(String(styles.transition)).map(
            (t: Omit<ITransitionItem, "id">): ITransitionItem => ({ ...t, id: uuidv4() })
          );
      appliedProperties.push("transition");
      touchSection("effects");
    } else {
      skippedProperties.push("transition");
    }
  }

  if (styles.transform !== undefined) {
    if (canApply("effects", "effects_transforms")) {
      const eff = ensureSection(styleObj, "effects");
      if (isExplicitClearValue(styles.transform)) {
        eff.transforms = [];
        eff.perspective = undefined;
      } else {
        const parsed = StyleHelper.parseTransformCss(String(styles.transform));
        eff.transforms = parsed.transforms.map((t: Omit<ITransformItem, "id">): ITransformItem => ({
          ...t,
          id: uuidv4()
        }));
        // perspective rides the same `transform` string as a leading function;
        // only overwrite when the value actually carries one.
        if (parsed.perspective !== undefined) {
          eff.perspective = parsed.perspective;
        }
      }
      appliedProperties.push("transform");
      touchSection("effects");
    } else {
      skippedProperties.push("transform");
    }
  }

  if (styles.filter !== undefined) {
    if (canApply("effects", "effects_filters")) {
      const eff = ensureSection(styleObj, "effects");
      eff.filters = isExplicitClearValue(styles.filter)
        ? []
        : StyleHelper.parseFilterCss(String(styles.filter)).map((f: Omit<IFilterItem, "id">): IFilterItem => ({
            ...f,
            id: uuidv4()
          }));
      appliedProperties.push("filter");
      touchSection("effects");
    } else {
      skippedProperties.push("filter");
    }
  }

  if (styles.backdropFilter !== undefined) {
    if (canApply("effects", "effects_backdrop_filters")) {
      const eff = ensureSection(styleObj, "effects");
      eff.backdropFilters = isExplicitClearValue(styles.backdropFilter)
        ? []
        : StyleHelper.parseFilterCss(String(styles.backdropFilter)).map((f: Omit<IFilterItem, "id">): IFilterItem => ({
            ...f,
            id: uuidv4()
          }));
      appliedProperties.push("backdropFilter");
      touchSection("effects");
    } else {
      skippedProperties.push("backdropFilter");
    }
  }

  // --- State styling: lift base changes into the requested state sub-object ---
  // When `options.state` is provided, the apply loop above wrote into base
  // section properties (text.color, fill.color, position.paddingTop, ...).
  // Move every actually-changed property into the matching {state}Styles bag
  // and revert the base to its pre-call value. Anything not in the allowed
  // list is reverted and reported as skipped so the caller can warn the user.
  if (options?.state) {
    const liftResult = liftSectionChangesToState(styleObj, originalStyleObjSnapshot, options.state, touchedSections);
    for (const unsupported of liftResult.unsupportedProperties) {
      skippedProperties.push(`${unsupported} (not state-eligible for ${options.state})`);
    }
    // State styles do not dislodge project style tokens on the base section,
    // so skip the inline-override ID clearing entirely.
    normalizeStyleObjIds(styleObj);
    if (options?.shouldCommit === false) {
      const sdState = getValues(sd);
      applyPropertyGridChangeWithoutCommit(
        sdState,
        { propertyName: "styleObj", value: styleObj, kuikaId },
        ESdChangeReason.ChangeStyleValue
      );
    } else {
      applyDesignerPropertyChange(sd, { propertyName: "styleObj", value: styleObj, kuikaId }, 22);
    }
    return { success: true, appliedProperties, skippedProperties };
  }

  // --- Clear token IDs for sections that have inline overrides ---
  // `convertFormStyleToDiStyle` (kuika-cl-model) rebuilds `*Id` from `*Ids` on
  // every commit.  The designer also repopulates `*Ids` between tool calls.
  // Therefore we must clear BOTH `*Id` and `*Ids` whenever a section already
  // contains inline properties — not only when THIS call adds new ones.
  const txt = styleObj.text as Record<string, unknown> | undefined;
  if (
    txt &&
    (touchedSections.has("text") ||
      txt.fontSize != null ||
      txt.fontWeight != null ||
      txt.color != null ||
      txt.fontFamily != null)
  ) {
    txt.typoId = null;
    txt.typoIds = [];
  }

  const fill = styleObj.fill as Record<string, unknown> | undefined;
  if (fill && (touchedSections.has("fill") || fill.color != null || fill.backgroundImage != null)) {
    fill.fillId = null;
    fill.fillIds = [];
  }

  const pos = styleObj.position as Record<string, unknown> | undefined;
  if (
    pos &&
    (touchedSections.has("position") ||
      pos.paddingTop != null ||
      pos.width != null ||
      pos.height != null ||
      pos.textAlign != null ||
      pos.alignItems != null ||
      pos.display != null)
  ) {
    pos.positionId = null;
    pos.positionIds = [];
  }

  const border = styleObj.border as Record<string, unknown> | undefined;
  if (
    border &&
    (touchedSections.has("border") ||
      border.borderTopLeftRadius != null ||
      border.borderTopWidth != null ||
      border.borderColor != null ||
      border.borderStyle != null)
  ) {
    border.borderId = null;
    border.borderIds = [];
  }

  const shadow = styleObj.shadow as Record<string, unknown> | undefined;
  if (
    shadow &&
    (touchedSections.has("shadow") || shadow.x != null || shadow.y != null || shadow.b != null || shadow.color != null)
  ) {
    shadow.shadowId = null;
    shadow.shadowIds = [];
  }

  // --- Normalize all style section IDs ---
  // addDefaultComponentStylings may produce malformed IDs (e.g. "|undefined", "|")
  // when component definitions lack default styling values. Clean up any sections
  // that weren't already cleared by the inline-override logic above.
  normalizeStyleObjIds(styleObj);

  // Apply the merged styleObj
  if (options?.shouldCommit === false) {
    const sdState = getValues(sd);
    applyPropertyGridChangeWithoutCommit(
      sdState,
      { propertyName: "styleObj", value: styleObj, kuikaId },
      ESdChangeReason.ChangeStyleValue
    );
  } else {
    applyDesignerPropertyChange(sd, { propertyName: "styleObj", value: styleObj, kuikaId }, 22);
  }

  return { success: true, appliedProperties, skippedProperties };
}

/**
 * Normalizes all style section IDs in a styleObj, cleaning up malformed values
 * produced by addDefaultComponentStylings when component definitions lack
 * default styling values (e.g. "|undefined", "|", ["", null]).
 *
 * For each section (text, fill, border, position, shadow):
 * - Filters *Ids arrays to only valid non-empty strings
 * - Rebuilds *Id as pipe-joined valid IDs, or null if none
 *
 * Safe to call on any styleObj — no-ops on already-clean data.
 */
export function normalizeStyleObjIds(styleObj: Record<string, any> | undefined): void {
  if (!styleObj) return;

  const isValidId = (id: unknown): id is string =>
    typeof id === "string" && id.length > 0 && id !== "undefined" && id !== "null";

  const normalizePipeId = (pipeId: unknown): string | null => {
    if (typeof pipeId !== "string" || pipeId.length === 0) return null;
    const valid = pipeId.split("|").filter(isValidId);
    return valid.length > 0 ? valid.join("|") : null;
  };

  const normalizeIdsArray = (ids: unknown): string[] => {
    if (!Array.isArray(ids)) return [];
    return ids.filter(isValidId);
  };

  const sections: Array<{ key: string; idField: string; idsField: string }> = [
    { key: "text", idField: "typoId", idsField: "typoIds" },
    { key: "fill", idField: "fillId", idsField: "fillIds" },
    { key: "border", idField: "borderId", idsField: "borderIds" },
    { key: "position", idField: "positionId", idsField: "positionIds" },
    { key: "shadow", idField: "shadowId", idsField: "shadowIds" }
  ];

  for (const { key, idField, idsField } of sections) {
    const section = styleObj[key] as Record<string, unknown> | undefined;
    if (!section) continue;

    // Only normalize if the ID or IDs array appear malformed
    const currentId = section[idField];
    const currentIds = section[idsField];

    // Check if *Id needs normalization (contains "undefined", starts with "|", etc.)
    if (typeof currentId === "string") {
      section[idField] = normalizePipeId(currentId);
    }

    // Check if *Ids array needs normalization (contains null, undefined, empty strings)
    if (Array.isArray(currentIds)) {
      const cleaned = normalizeIdsArray(currentIds);
      // Only update if the array actually changed (avoid unnecessary mutations)
      if (cleaned.length !== currentIds.length || currentIds.some((id: unknown) => !isValidId(id))) {
        section[idsField] = cleaned;
      }
    }
  }
}

// --- Reverse enum maps for simplifyStyleObj ---
const REVERSE_ALIGN_ITEMS: Record<number, string> = { 1: "flex-start", 2: "center", 3: "flex-end" };
const REVERSE_TEXT_ALIGN: Record<number, string> = { 1: "left", 2: "center", 3: "right" };
const REVERSE_DISPLAY: Record<number, string> = {
  1: "inline-block",
  2: "inline-flex",
  3: "grid",
  4: "block",
  5: "inline",
  6: "none"
};

/**
 * Combines a numeric dimension value with its unit style string.
 * e.g. (100, "px") → "100px", (50, "%") → "50%"
 * Returns null if value is null/undefined/0.
 */
function combineDimension(value: any, style: any): string | null {
  if (value == null || value === 0) return null;
  const unit = style || "px";
  return `${value}${unit}`;
}

/**
 * Parses a pipe-delimited style ID string (e.g. "guid1|guid2") into an array of lowercase IDs.
 * Returns empty array if null/undefined/empty.
 */
function parseStyleIds(pipeDelimited: string | null | undefined): string[] {
  if (!pipeDelimited || pipeDelimited.length === 0) return [];
  return pipeDelimited
    .split("|")
    .map((id) => id.trim().toLowerCase())
    .filter((id) => id.length > 0);
}

interface IProjectStyleRef {
  id: string;
  name: string;
  section: string;
  values: Record<string, unknown>;
}

/**
 * Resolves project-level style references from a component's styleObj against the project's
 * reusable style arrays. Returns an array of resolved style references with their names and values.
 */
export function resolveProjectStyles(styleObj: unknown, project: IProject | null | undefined): IProjectStyleRef[] {
  if (!styleObj || typeof styleObj !== "object" || !project) return [];

  const style = styleObj as Record<string, Record<string, unknown> | undefined>;
  const refs: IProjectStyleRef[] = [];

  // Typography
  const typoIds = parseStyleIds(style.text?.typoId as string);
  if (typoIds.length > 0 && project.stylingTypography) {
    for (const id of typoIds) {
      const match = project.stylingTypography.find((s) => s.typoId?.toLowerCase() === id);
      if (match) {
        const values: Record<string, unknown> = {};
        if (match.fontFamily) values.fontFamily = match.fontFamily;
        if (match.fontWeight != null) values.fontWeight = match.fontWeight;
        if (match.fontSize != null) values.fontSize = match.fontSize;
        if (match.color) values.color = match.color;
        if (match.lineHeight != null) values.lineHeight = match.lineHeight;
        if (match.letterSpacing != null) values.letterSpacing = match.letterSpacing;
        if (match.textDecoration && match.textDecoration !== "none") values.textDecoration = match.textDecoration;
        if (match.textTransform && match.textTransform !== "none") values.textTransform = match.textTransform;
        refs.push({ id, name: match.name || id, section: "text", values });
      }
    }
  }

  // Fill
  const fillIds = parseStyleIds(style.fill?.fillId as string);
  if (fillIds.length > 0 && project.stylingFill) {
    for (const id of fillIds) {
      const match = project.stylingFill.find((s) => s.fillId?.toLowerCase() === id);
      if (match) {
        const values: Record<string, unknown> = {};
        if (match.color != null) {
          if (isGradient(match.color)) {
            // Raw CSS preserved for all gradient types (radial/conic safe);
            // structural backgroundGradient only when linear-parseable.
            const gradient = parseGradientCss(match.color);
            if (gradient) {
              values.backgroundGradient = gradient;
            }
            values.backgroundGradientCss = match.color;
          } else {
            values.backgroundColor = match.color;
          }
        }
        if (match.backgroundImage) values.backgroundImage = match.backgroundImage;
        refs.push({ id, name: match.name || id, section: "fill", values });
      }
    }
  }

  // Border
  const borderIds = parseStyleIds(style.border?.borderId as string);
  if (borderIds.length > 0 && project.stylingBorder) {
    for (const id of borderIds) {
      const match = project.stylingBorder.find((s) => s.borderId?.toLowerCase() === id);
      if (match) {
        const values: Record<string, unknown> = {};
        if (match.borderColor) values.borderColor = match.borderColor;
        if (match.borderStyle) values.borderStyle = match.borderStyle;
        const bw = [match.borderTopWidth, match.borderRightWidth, match.borderBottomWidth, match.borderLeftWidth];
        const nonNullBw = bw.filter((v) => v != null);
        if (nonNullBw.length > 0) {
          if (nonNullBw.every((v) => v === nonNullBw[0])) {
            values.borderWidth = nonNullBw[0];
          } else {
            if (match.borderTopWidth != null) values.borderTopWidth = match.borderTopWidth;
            if (match.borderRightWidth != null) values.borderRightWidth = match.borderRightWidth;
            if (match.borderBottomWidth != null) values.borderBottomWidth = match.borderBottomWidth;
            if (match.borderLeftWidth != null) values.borderLeftWidth = match.borderLeftWidth;
          }
        }
        const br = [
          match.borderTopLeftRadius,
          match.borderTopRightRadius,
          match.borderBottomRightRadius,
          match.borderBottomLeftRadius
        ];
        const nonNullBr = br.filter((v) => v != null);
        if (nonNullBr.length > 0) {
          if (nonNullBr.every((v) => v === nonNullBr[0])) {
            values.borderRadius = nonNullBr[0];
          } else {
            if (match.borderTopLeftRadius != null) values.borderTopLeftRadius = match.borderTopLeftRadius;
            if (match.borderTopRightRadius != null) values.borderTopRightRadius = match.borderTopRightRadius;
            if (match.borderBottomRightRadius != null) values.borderBottomRightRadius = match.borderBottomRightRadius;
            if (match.borderBottomLeftRadius != null) values.borderBottomLeftRadius = match.borderBottomLeftRadius;
          }
        }
        refs.push({ id, name: match.name || id, section: "border", values });
      }
    }
  }

  // Shadow
  const shadowIds = parseStyleIds(style.shadow?.shadowId as string);
  if (shadowIds.length > 0 && project.stylingShadow) {
    for (const id of shadowIds) {
      const match = project.stylingShadow.find((s) => s.shadowId?.toLowerCase() === id);
      if (match) {
        const values: Record<string, unknown> = {};
        if (match.x != null) values.x = match.x;
        if (match.y != null) values.y = match.y;
        if (match.b != null) values.blur = match.b;
        if (match.s != null) values.spread = match.s;
        if (match.color) values.color = match.color;
        refs.push({ id, name: match.name || id, section: "shadow", values });
      }
    }
  }

  // Position
  const positionIds = parseStyleIds(style.position?.positionId as string);
  if (positionIds.length > 0 && project.stylingPosition) {
    for (const id of positionIds) {
      const match = project.stylingPosition.find((s) => s.positionId?.toLowerCase() === id);
      if (match) {
        const values: Record<string, unknown> = {};
        if (match.paddingTop != null) values.paddingTop = match.paddingTop;
        if (match.paddingRight != null) values.paddingRight = match.paddingRight;
        if (match.paddingBottom != null) values.paddingBottom = match.paddingBottom;
        if (match.paddingLeft != null) values.paddingLeft = match.paddingLeft;
        const w = combineDimension(match.width, match.widthStyle);
        if (w) values.width = w;
        const h = combineDimension(match.height, match.heightStyle);
        if (h) values.height = h;
        if (match.textAlign != null && REVERSE_TEXT_ALIGN[match.textAlign])
          values.textAlign = REVERSE_TEXT_ALIGN[match.textAlign];
        if (match.alignItems != null && REVERSE_ALIGN_ITEMS[match.alignItems])
          values.alignItems = REVERSE_ALIGN_ITEMS[match.alignItems];
        if (match.display != null && REVERSE_DISPLAY[match.display]) values.display = REVERSE_DISPLAY[match.display];
        refs.push({ id, name: match.name || id, section: "position", values });
      }
    }
  }

  return refs;
}

/**
 * Merges resolved project style values into inline styles to produce the effective computed styles.
 * Project styles form the base; inline values override on top (same cascade as CSS: class < inline).
 */
export function computeEffectiveStyles(
  inlineStyles: Record<string, any> | null,
  projectStyles: IProjectStyleRef[]
): Record<string, any> | null {
  if (projectStyles.length === 0) return inlineStyles;

  const computed: Record<string, Record<string, any>> = {};

  // Apply project styles first (base layer)
  for (const ps of projectStyles) {
    const section = ps.section;
    if (!computed[section]) computed[section] = {};
    for (const [key, value] of Object.entries(ps.values)) {
      if (value != null) {
        computed[section][key] = value;
      }
    }
  }

  // Apply inline styles on top (override layer)
  if (inlineStyles) {
    for (const [section, sectionValues] of Object.entries(inlineStyles)) {
      if (!computed[section]) computed[section] = {};
      if (sectionValues && typeof sectionValues === "object") {
        for (const [key, value] of Object.entries(sectionValues as Record<string, unknown>)) {
          if (value != null) {
            computed[section][key] = value;
          }
        }
      }
    }
  }

  // Remove empty sections
  for (const section of Object.keys(computed)) {
    if (Object.keys(computed[section]).length === 0) {
      delete computed[section];
    }
  }

  return Object.keys(computed).length > 0 ? computed : null;
}

/**
 * Converts internal IKuikaStyle (di.styleObj) to a compact CSS-like object.
 * Strips all internal metadata (IDs, locks, visibility, kuikaid, componentType, overridden, etc.)
 * and converts enums/dimensions to human-readable CSS values.
 *
 * Output shape:
 * {
 *   text?: { fontSize, fontWeight, color, fontFamily, lineHeight, letterSpacing, textDecoration, textTransform },
 *   fill?: { backgroundColor },
 *   position?: { paddingTop/Right/Bottom/Left, width, height, minWidth, minHeight, maxWidth, maxHeight, textAlign, alignItems, display },
 *   border?: { borderTopWidth, ..., borderTopLeftRadius, ..., borderColor, borderStyle },
 *   shadow?: { x, y, blur, spread, color }
 * }
 */
export function simplifyStyleObj(styleObj: any): Record<string, any> | null {
  if (!styleObj) return null;

  const result: Record<string, any> = {};

  // --- Text section ---
  if (styleObj.text) {
    const t = styleObj.text;
    const text: Record<string, any> = {};
    if (t.fontSize != null) text.fontSize = t.fontSize;
    if (t.fontWeight != null) text.fontWeight = t.fontWeight;
    if (t.color != null) text.color = t.color;
    if (t.fontFamily != null) text.fontFamily = t.fontFamily;
    if (t.lineHeight != null) text.lineHeight = t.lineHeight;
    if (t.letterSpacing != null) text.letterSpacing = t.letterSpacing;
    if (t.textDecoration && t.textDecoration !== "" && t.textDecoration !== "none")
      text.textDecoration = t.textDecoration;
    if (t.textTransform && t.textTransform !== "" && t.textTransform !== "none") text.textTransform = t.textTransform;
    if (Object.keys(text).length > 0) result.text = text;
  }

  // --- Fill section ---
  if (styleObj.fill) {
    const f = styleObj.fill;
    const fill: Record<string, any> = {};
    if (f.color != null && f.color !== "" && f.color !== "transparent") {
      if (isGradient(f.color)) {
        // Raw CSS preserved for all gradient types (radial/conic safe) so the
        // codec emits a valid `background-image`; structural backgroundGradient
        // is only populated when the gradient is linear-parseable.
        const gradient = parseGradientCss(f.color);
        if (gradient) {
          fill.backgroundGradient = gradient;
        }
        fill.backgroundGradientCss = f.color;
      } else {
        fill.backgroundColor = f.color;
      }
    }
    if (f.backgroundImage != null) fill.backgroundImage = f.backgroundImage;
    if (Object.keys(fill).length > 0) result.fill = fill;
  }

  // --- Position section ---
  if (styleObj.position) {
    const p = styleObj.position;
    const pos: Record<string, any> = {};

    // Padding
    if (p.paddingTop != null) pos.paddingTop = p.paddingTop;
    if (p.paddingRight != null) pos.paddingRight = p.paddingRight;
    if (p.paddingBottom != null) pos.paddingBottom = p.paddingBottom;
    if (p.paddingLeft != null) pos.paddingLeft = p.paddingLeft;

    // Dimensions (combine value + unit)
    const w = combineDimension(p.width, p.widthStyle);
    if (w) pos.width = w;
    const h = combineDimension(p.height, p.heightStyle);
    if (h) pos.height = h;
    const minW = combineDimension(p.minWidth, p.minWidthStyle);
    if (minW) pos.minWidth = minW;
    const minH = combineDimension(p.minHeight, p.minHeightStyle);
    if (minH) pos.minHeight = minH;
    const maxW = combineDimension(p.maxWidth, p.maxWidthStyle);
    if (maxW) pos.maxWidth = maxW;
    const maxH = combineDimension(p.maxHeight, p.maxHeightStyle);
    if (maxH) pos.maxHeight = maxH;

    // Enums → CSS strings
    if (p.textAlign != null && REVERSE_TEXT_ALIGN[p.textAlign]) pos.textAlign = REVERSE_TEXT_ALIGN[p.textAlign];
    if (p.alignItems != null && REVERSE_ALIGN_ITEMS[p.alignItems]) pos.alignItems = REVERSE_ALIGN_ITEMS[p.alignItems];
    if (p.display != null && REVERSE_DISPLAY[p.display]) pos.display = REVERSE_DISPLAY[p.display];

    if (Object.keys(pos).length > 0) result.position = pos;
  }

  // --- Border section ---
  if (styleObj.border) {
    const b = styleObj.border;
    const border: Record<string, any> = {};

    // Border widths — collapse to single if all equal; skip 0 (default/not set)
    const bw = [b.borderTopWidth, b.borderRightWidth, b.borderBottomWidth, b.borderLeftWidth];
    const nonDefaultBw = bw.filter((v: any) => v != null && v !== 0);
    if (nonDefaultBw.length > 0) {
      const allEqual = nonDefaultBw.every((v: any) => v === nonDefaultBw[0]) && nonDefaultBw.length === 4;
      if (allEqual) {
        border.borderWidth = nonDefaultBw[0];
      } else {
        if (b.borderTopWidth != null && b.borderTopWidth !== 0) border.borderTopWidth = b.borderTopWidth;
        if (b.borderRightWidth != null && b.borderRightWidth !== 0) border.borderRightWidth = b.borderRightWidth;
        if (b.borderBottomWidth != null && b.borderBottomWidth !== 0) border.borderBottomWidth = b.borderBottomWidth;
        if (b.borderLeftWidth != null && b.borderLeftWidth !== 0) border.borderLeftWidth = b.borderLeftWidth;
      }
    }

    // Border radii — collapse to single if all equal; skip 0 (default/not set)
    const br = [b.borderTopLeftRadius, b.borderTopRightRadius, b.borderBottomRightRadius, b.borderBottomLeftRadius];
    const nonDefaultBr = br.filter((v: any) => v != null && v !== 0);
    if (nonDefaultBr.length > 0) {
      const allEqual = nonDefaultBr.every((v: any) => v === nonDefaultBr[0]) && nonDefaultBr.length === 4;
      if (allEqual) {
        border.borderRadius = nonDefaultBr[0];
      } else {
        if (b.borderTopLeftRadius != null && b.borderTopLeftRadius !== 0)
          border.borderTopLeftRadius = b.borderTopLeftRadius;
        if (b.borderTopRightRadius != null && b.borderTopRightRadius !== 0)
          border.borderTopRightRadius = b.borderTopRightRadius;
        if (b.borderBottomRightRadius != null && b.borderBottomRightRadius !== 0)
          border.borderBottomRightRadius = b.borderBottomRightRadius;
        if (b.borderBottomLeftRadius != null && b.borderBottomLeftRadius !== 0)
          border.borderBottomLeftRadius = b.borderBottomLeftRadius;
      }
    }

    if (b.borderColor != null && b.borderColor !== "transparent") border.borderColor = b.borderColor;
    if (b.borderStyle != null && b.borderStyle !== "solid") border.borderStyle = b.borderStyle;

    if (Object.keys(border).length > 0) result.border = border;
  }

  // --- Shadow section ---
  if (styleObj.shadow) {
    const s = styleObj.shadow;
    const shadow: Record<string, any> = {};
    if (s.x != null) shadow.x = s.x;
    if (s.y != null) shadow.y = s.y;
    if (s.b != null) shadow.blur = s.b;
    if (s.s != null) shadow.spread = s.s;
    if (s.color != null) shadow.color = s.color;
    if (s.isInset === true) shadow.isInset = true;
    if (Object.keys(shadow).length > 0) result.shadow = shadow;
  }

  return Object.keys(result).length > 0 ? result : null;
}
